/**
 * Poppa Friends – átvételi pont választó a kosárban (snippets/poppa-atveteli-pont.liquid).
 *
 * - A vásárló kiválasztja, hogyan kéri a csomagot; csomagpontos módnál a szolgáltató hivatalos térképén pontot választ.
 * - A választás kosárattribútumként mentődik (/cart/update.js), a Shopify ezt a rendeléshez másolja.
 * - Fizetéshez csak akkor enged tovább, ha van mód, és csomagpontos módnál van kiválasztott pont.
 *
 * GLS: hivatalos, díjmentes térkép (<gls-dpm-dialog>, map.gls-hungary.com), kulcs nélkül.
 * Titkos kulcs nem kerül a böngészőbe; a szkript csak nyilvános adatokkal dolgozik.
 */

const ATTR = {
  carrier: 'Szállító',
  mode: 'Átvételi mód',
  type: 'Átvételi pont típusa',
  id: 'Átvételi pont azonosító',
  name: 'Átvételi pont neve',
  address: 'Átvételi pont címe',
};

/** A csomagpontos módok: szolgáltató és a térkép szűrője. A nevek egyeznek a Shopify-szállítási módokkal. */
const POINT_MODES = {
  'GLS csomagautomata': { carrier: 'GLS', filter: 'parcel-locker', typeLabel: 'GLS csomagautomata' },
  'GLS Csomagpont': { carrier: 'GLS', filter: 'parcel-shop', typeLabel: 'GLS csomagpont' },
  'MPL Csomagautomata': {
    carrier: 'MPL',
    filter: 'mpl-locker',
    typeLabel: 'MPL csomagautomata',
    src: 'mplSrc',
    title: 'MPL csomagautomata kiválasztása',
    countLabel: 'MPL csomagautomata',
  },
  'MPL Posta Pont': {
    carrier: 'MPL',
    filter: 'mpl-post',
    typeLabel: 'MPL postai átvételi pont',
    src: 'mplPostSrc',
    title: 'Posta vagy PostaPont kiválasztása',
    countLabel: 'posta és PostaPont',
  },
};

/** Ingyenes térképkönyvtár (Leaflet) a cdnjs-ről, OpenStreetMap-csempékkel – az MPL-térképhez. */
const LEAFLET = {
  css: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css',
  js: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js',
};
const MPL_COLOR = '#c8102e';
const BUDAPEST = [47.4979, 19.0402];

/** Házhozszállítás szolgáltatónként (a nevek egyeznek a Shopify-szállítási módokkal). */
const HOME_MODES = { 'GLS házhozszállítás': 'GLS', 'MPL Házhozszállítás': 'MPL' };
const GLS_LOAD_TIMEOUT = 15000;

/**
 * A pénztár előkészítése a Storefront API kosár-mutációival (token nélkül, minden csomagon elérhető):
 * csomagpontnál a pont címe lesz a pénztár szállítási címe, és a kosárban választott szállítási mód lesz bejelölve.
 * Basic csomagon a pénztár többi szállítási módja nem rejthető el – ezt a Shopify nem engedi.
 */
const STOREFRONT_API = 'api/2026-01/graphql.json';

/**
 * Szállítási jelölők: 0 Ft-os, rejtett (UNLISTED) termékek, mindegyik a saját szállítási profiljában, amelyben csak
 * az adott mód van. A Shopify a több profilból álló kosárnál csak az azonos nevű módokat kínálja (díjaik összeadódnak),
 * így a jelölővel a pénztárban szerveroldalon csak a kosárban választott mód választható – a díj a fő profilé.
 */
const MARKER_TYPE = 'Szállítási jelölő';
/**
 * Feltétel: a Shopify adminban a „Show split shipping in checkout” (megosztott szállítás) KI van kapcsolva – különben a
 * jelölő külön szállítmányt képez, és a pénztár más módokat is felkínál. Hamisra állítva a kosárban maradt jelölők kikerülnek.
 */
const MARKERS_ENABLED = true;
const MARKERS = {
  'GLS csomagautomata': 'szallitas-gls-csomagautomata',
  'GLS Csomagpont': 'szallitas-gls-csomagpont',
  'MPL Csomagautomata': 'szallitas-mpl-csomagautomata',
  'MPL Posta Pont': 'szallitas-mpl-posta-pont',
  'GLS házhozszállítás': 'szallitas-gls-hazhozszallitas',
  'MPL Házhozszállítás': 'szallitas-mpl-hazhozszallitas',
};

/** Az aktuális állapot (a Liquid által kirajzolt első példányból indul). */
const state = { mode: '', point: null };

/**
 * A Shopify által a kosárra számolt szállítási módok és díjak (Storefront API, a kosár szállítási csoportjai).
 * undefined = még nincs adat, null = nem sikerült lekérni, [] = a Shopify nem kínál szállítást.
 * A díjzónák országosak; a díjat nem a téma találja ki.
 * @type {Array<{name: string, price: number}> | null | undefined}
 */
let rates;
/** @type {Promise<unknown> | null} */
let pending = null;

function roots() {
  return Array.from(document.querySelectorAll('[data-pf-pickup]'));
}

function readInitialState() {
  const root = roots()[0];
  if (!root) return;
  state.mode = root.dataset.mode || '';
  const id = root.dataset.pointId || '';
  if (id) {
    state.point = {
      id,
      name: root.querySelector('[data-pf-pickup-name]')?.textContent?.trim() || '',
      address: root.querySelector('[data-pf-pickup-address]')?.textContent?.trim() || '',
    };
  }
}

function hintText() {
  if (!state.mode) return '';
  const article = (POINT_MODES[state.mode]?.carrier || HOME_MODES[state.mode]) === 'MPL' ? 'az' : 'a';
  if (HOME_MODES[state.mode]) {
    return `A pénztárban add meg a nevedet, a telefonszámodat és a szállítási címedet, majd ellenőrizd, hogy ${article} „${state.mode}” legyen bejelölve.`;
  }
  return `A pénztárban ${article} „${state.mode}” mód lesz bejelölve, és a pont címe már ki lesz töltve: csak a nevedet és a telefonszámodat kell megadnod. Számlázási címként a saját címedet add meg.`;
}

/** Minden példány (kosároldal, fiók) frissítése az állapotból. */
function render() {
  roots().forEach((root, index) => {
    root.dataset.mode = state.mode;
    root.dataset.pointId = state.point?.id || '';
    for (const input of root.querySelectorAll('[data-pf-pickup-mode]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      // Kosároldal és fiók: külön rádiócsoport, különben a böngésző egymás választását törölné.
      input.name = `pf-pickup-mode-${index}`;
      input.checked = input.value === state.mode;
    }
    const needsPoint = Boolean(POINT_MODES[state.mode]);
    const pointBox = root.querySelector('[data-pf-pickup-point]');
    if (pointBox instanceof HTMLElement) pointBox.hidden = !needsPoint;
    const chosen = root.querySelector('[data-pf-pickup-chosen]');
    if (chosen instanceof HTMLElement) chosen.hidden = !state.point;
    const set = (selector, text) => {
      const el = root.querySelector(selector);
      if (el) el.textContent = text;
    };
    set('[data-pf-pickup-name]', state.point?.name || '');
    set('[data-pf-pickup-address]', state.point?.address || '');
    set('[data-pf-pickup-id]', state.point?.id || '');
    set('[data-pf-pickup-open]', state.point ? 'Másik pont választása' : 'Átvételi pont kiválasztása a térképen');
    const hint = root.querySelector('[data-pf-pickup-hint]');
    if (hint instanceof HTMLElement) {
      hint.textContent = hintText();
      hint.hidden = !state.mode;
    }
  });
  renderFees();
}

/* ---------- Szállítási díjak a Shopifyból ---------- */

function formatFee(price) {
  // A téma árformátuma: „1.290 Ft” (pontos ezres tagolás)
  return price === 0 ? 'Ingyenes' : `${String(Math.round(price)).replace(/\B(?=(\d{3})+(?!\d))/g, '.')} Ft`;
}

/** A módhoz tartozó Shopify-díjak: csomagpontos módnál a pontos név, házhozszállításnál minden „házhozszállítás” mód. */
function ratesFor(mode) {
  if (!Array.isArray(rates)) return [];
  const lower = (s) => s.toLocaleLowerCase('hu');
  return rates.filter((r) => lower(r.name) === lower(mode));
}

function feeText(mode) {
  if (!Array.isArray(rates)) return '';
  const found = ratesFor(mode);
  if (!found.length) return '–';
  const prices = [...new Set(found.map((r) => r.price))].sort((a, b) => a - b);
  return prices.length === 1 ? formatFee(prices[0]) : `${formatFee(prices[0])} – ${formatFee(prices[prices.length - 1])}`;
}

function warningText() {
  if (!Array.isArray(rates)) return '';
  if (!rates.length) return 'A Shopify ehhez a kosárhoz most nem talált szállítási módot. Kérjük, jelezd nekünk, vagy módosítsd a kosarat.';
  if (state.mode && !ratesFor(state.mode).length) {
    return `Ehhez a kosárhoz a Shopify jelenleg nem kínál „${state.mode}” szállítást. A pénztárban az ott megjelenő módok közül választhatsz.`;
  }
  return '';
}

function renderFees() {
  for (const root of roots()) {
    for (const el of root.querySelectorAll('[data-pf-fee]')) {
      if (el instanceof HTMLElement) el.textContent = feeText(el.dataset.pfFee || '');
    }
    // Összesítő a vásárlónak: választott szállítás, díja, és a szállítással együtt fizetendő összeg.
    const summary = root.querySelector('[data-pf-summary]');
    if (summary instanceof HTMLElement) {
      const fee = ratesFor(state.mode)[0];
      summary.hidden = !state.mode || !fee || cartTotal == null;
      if (!summary.hidden) {
        const set = (selector, text) => {
          const el = summary.querySelector(selector);
          if (el) el.textContent = text;
        };
        set('[data-pf-summary-mode]', state.mode);
        set('[data-pf-summary-fee]', formatFee(fee.price));
        set('[data-pf-summary-total]', formatFee(Number(cartTotal) + fee.price).replace('Ingyenes', '0 Ft'));
      }
    }
    const warning = root.querySelector('[data-pf-fee-warning]');
    if (warning instanceof HTMLElement) {
      const text = warningText();
      warning.textContent = text;
      warning.hidden = !text;
    }
  }
}

let ratesTimer = 0;
let ratesRequest = 0;
/**
 * A díjak egy ideiglenes Storefront-kosárból: ugyanazok a termékek a szállítási jelölő nélkül, csak az ország megadásával.
 * (A vásárló kosarában a jelölő miatt csak a választott mód szerepel; a többi mód díját így is meg tudjuk mutatni.)
 * A vásárló kosarához a díjlekérés nem nyúl. Azonos termékösszetételnél a korábbi eredményt használjuk.
 */
let ratesKey = '';
/** A kosár termékeinek végösszege forintban (a Shopify számítása, kedvezményekkel), az összesítőhöz. @type {number | null} */
let cartTotal = null;
async function refreshRates() {
  if (!roots().length) return;
  const request = ++ratesRequest;
  const base = window.Shopify?.routes?.root || '/';
  try {
    const cart = await (await fetch(`${base}cart.js`)).json();
    cartTotal = cart.total_price / 100;
    const lines = cart.items
      .filter((item) => item.product_type !== MARKER_TYPE)
      .map((item) => ({ merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`, quantity: item.quantity }));
    const key = JSON.stringify(lines);
    if (key === ratesKey && Array.isArray(rates)) return renderFees();
    if (!lines.length) {
      rates = [];
      ratesKey = key;
      return renderFees();
    }
    const data = await storefront(
      `mutation($input: CartInput!) { cartCreate(input: $input) {
        cart { deliveryGroups(first: 10) { nodes { deliveryOptions { title estimatedCost { amount } } } } }
        userErrors { message }
      } }`,
      {
        input: {
          lines,
          delivery: { addresses: [{ selected: true, oneTimeUse: true, validationStrategy: 'COUNTRY_CODE_ONLY', address: { deliveryAddress: { countryCode: 'HU' } } }] },
        },
      }
    );
    if (request !== ratesRequest) return; // újabb kosárváltozás jött
    ratesKey = key;
    const groups = data.cartCreate?.cart?.deliveryGroups?.nodes || [];
    // Több szállítási csoportnál (eltérő profilok) csak a mindegyikben elérhető módot mutatjuk, összegzett díjjal.
    const byTitle = new Map();
    groups.forEach((group, index) => {
      for (const option of group.deliveryOptions) {
        const entry = byTitle.get(option.title) || { name: option.title, price: 0, count: 0 };
        if (entry.count === index) {
          entry.price += Number(option.estimatedCost.amount);
          entry.count += 1;
        }
        byTitle.set(option.title, entry);
      }
    });
    rates = [...byTitle.values()].filter((r) => r.count === groups.length && groups.length > 0).map(({ name, price }) => ({ name, price }));
  } catch {
    if (request !== ratesRequest) return;
    rates = null; // nem írunk ki összeget, csak azt, hogy a pénztárban jelenik meg
  }
  renderFees();
}

function scheduleRates() {
  clearTimeout(ratesTimer);
  ratesTimer = window.setTimeout(refreshRates, 300);
}

function showError(message, root) {
  for (const r of root ? [root] : roots()) {
    const el = r.querySelector('[data-pf-pickup-error]');
    if (el instanceof HTMLElement) {
      el.textContent = message;
      el.hidden = !message;
    }
  }
}

/** Az állapot mentése kosárattribútumba. Üres érték = az attribútum törlése. */
function save() {
  const mode = POINT_MODES[state.mode];
  const attributes = {
    [ATTR.mode]: state.mode,
    [ATTR.carrier]: mode ? mode.carrier : HOME_MODES[state.mode] || '',
    [ATTR.type]: mode && state.point ? mode.typeLabel : '',
    [ATTR.id]: state.point?.id || '',
    [ATTR.name]: state.point?.name || '',
    [ATTR.address]: state.point?.address || '',
  };
  const request = fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes }),
  })
    .then((response) => {
      if (!response.ok) throw new Error(`cart/update ${response.status}`);
      return response.json();
    })
    // A pénztár előkészítése (cím + bejelölt mód). Ha nem sikerül, a választás a kosárattribútumokban akkor is megvan.
    .then((cart) => syncCheckout().catch(() => {}).then(() => cart));
  pending = request;
  request
    .catch(() => showError('Nem sikerült menteni a választást. Ellenőrizd az internetkapcsolatot, és próbáld újra.'))
    .finally(() => {
      if (pending === request) pending = null;
    });
  return request;
}

async function storefront(query, variables) {
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}${STOREFRONT_API}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ query, variables }),
  });
  if (!response.ok) throw new Error(`storefront ${response.status}`);
  const data = await response.json();
  if (data.errors?.length) throw new Error(data.errors[0].message);
  return data.data;
}

/**
 * A pénztár előkészítése a kosár alapján:
 * - csomagpontnál a pont címe lesz a szállítási cím (a 2. címsorban a pont neve és azonosítója), és a mód bejelölve;
 * - házhozszállításnál csak az ország és a bejelölt mód; a címet a vásárló adja meg a pénztárban;
 * - mód nélkül törli a korábban beállított címet.
 */
let syncChain = Promise.resolve();
function syncCheckout() {
  syncChain = syncChain.catch(() => {}).then(runSyncCheckout);
  return syncChain;
}

/** @type {Map<string, Promise<number | null>>} */
const markerIds = new Map();
function markerVariantId(handle) {
  const base = window.Shopify?.routes?.root || '/';
  if (!markerIds.has(handle)) {
    markerIds.set(
      handle,
      fetch(`${base}products/${handle}.js`)
        .then((response) => (response.ok ? response.json() : null))
        .then((product) => product?.variants?.[0]?.id ?? null)
        .catch(() => null)
    );
  }
  return /** @type {Promise<number | null>} */ (markerIds.get(handle));
}

function postJson(url, body) {
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  }).then((response) => {
    if (!response.ok) throw new Error(`${url} ${response.status}`);
    return response.json();
  });
}

/**
 * A kosárban pontosan a választott mód jelölője legyen (csomagpontnál a pont adataival mint tételtulajdonság),
 * minden más jelölő kerüljön ki. Termék nélküli kosárban nincs jelölő.
 */
async function syncMarker(cart) {
  const base = window.Shopify?.routes?.root || '/';
  const markers = cart.items.filter((item) => item.product_type === MARKER_TYPE);
  const hasProducts = cart.items.some((item) => item.product_type !== MARKER_TYPE);
  const pointMode = POINT_MODES[state.mode];
  const wanted = MARKERS_ENABLED && hasProducts && MARKERS[state.mode] && (!pointMode || state.point) ? MARKERS[state.mode] : null;
  const variantId = wanted ? await markerVariantId(wanted) : null;
  /** @type {Record<string, string>} */
  const properties = pointMode && state.point ? { 'Átvételi pont': state.point.name, Cím: state.point.address, Azonosító: state.point.id } : {};

  const same = (a, b) => {
    const keysA = Object.keys(a || {}).filter((k) => !k.startsWith('_'));
    const keysB = Object.keys(b);
    return keysA.length === keysB.length && keysB.every((k) => String(a[k]) === String(b[k]));
  };
  const upToDate = variantId
    ? markers.length === 1 && markers[0].variant_id === variantId && markers[0].quantity === 1 && same(markers[0].properties, properties)
    : markers.length === 0;
  if (upToDate) return cart;

  if (markers.length) {
    await postJson(`${base}cart/update.js`, { updates: Object.fromEntries(markers.map((m) => [m.key, 0])) });
  }
  if (variantId) {
    await postJson(`${base}cart/add.js`, { items: [{ id: variantId, quantity: 1, properties }] });
  }
  return (await fetch(`${base}cart.js`)).json();
}

async function runSyncCheckout() {
  const base = window.Shopify?.routes?.root || '/';
  const cart = await syncMarker(await (await fetch(`${base}cart.js`)).json());
  const cartId = `gid://shopify/Cart/${cart.token}`;
  const mode = POINT_MODES[state.mode];
  const point = state.point;

  // Újratöltés után a pont címe csak szövegként ismert: a már beállított pénztárcímet ilyenkor nem írjuk felül.
  const keepAddress = Boolean(mode && point && !point.street);
  if (!keepAddress) {
    /** @type {any[]} */
    let addresses = [];
    if (mode && point) {
      addresses = [
        {
          selected: true,
          oneTimeUse: true,
          validationStrategy: 'COUNTRY_CODE_ONLY',
          address: {
            deliveryAddress: {
              address1: point.street,
              address2: `${mode.typeLabel}: ${point.name} (${point.id})`.slice(0, 255),
              city: point.city,
              zip: point.zip || '',
              countryCode: 'HU',
            },
          },
        },
      ];
    } else if (HOME_MODES[state.mode]) {
      addresses = [{ selected: true, oneTimeUse: true, validationStrategy: 'COUNTRY_CODE_ONLY', address: { deliveryAddress: { countryCode: 'HU' } } }];
    }
    const replaced = await storefront(
      `mutation($id: ID!, $addresses: [CartSelectableAddressInput!]!) {
        cartDeliveryAddressesReplace(cartId: $id, addresses: $addresses) { userErrors { message } }
      }`,
      { id: cartId, addresses }
    );
    if (replaced.cartDeliveryAddressesReplace.userErrors.length) throw new Error(replaced.cartDeliveryAddressesReplace.userErrors[0].message);
  }
  if (!state.mode) return;

  const data = await storefront(
    `query($id: ID!) { cart(id: $id) { deliveryGroups(first: 10) { nodes { id deliveryOptions { handle title } } } } }`,
    { id: cartId }
  );
  const lower = (s) => String(s).toLocaleLowerCase('hu');
  const selections = (data.cart?.deliveryGroups?.nodes || [])
    .map((group) => {
      const option = group.deliveryOptions.find((o) => lower(o.title) === lower(state.mode));
      return option ? { deliveryGroupId: group.id, deliveryOptionHandle: option.handle } : null;
    })
    .filter(Boolean);
  if (!selections.length) return;
  await storefront(
    `mutation($id: ID!, $s: [CartSelectedDeliveryOptionInput!]!) {
      cartSelectedDeliveryOptionsUpdate(cartId: $id, selectedDeliveryOptions: $s) { userErrors { message } }
    }`,
    { id: cartId, s: selections }
  );
}

/** Budapesti kerület nélküli településnév a pénztár címéhez („Budapest V. ker.” → „Budapest”). */
function plainCity(city) {
  return String(city || '').replace(/\s+[IVXLC]+\.\s*ker\.$/i, '').trim();
}

/** A GLS hivatalos térképének betöltése (egyszer). */
let glsLoading = null;
function loadGls(src) {
  if (customElements.get('gls-dpm-dialog')) return Promise.resolve();
  if (!glsLoading) {
    glsLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onerror = () => reject(new Error('GLS script'));
      document.head.appendChild(script);
      const timer = setTimeout(() => reject(new Error('GLS timeout')), GLS_LOAD_TIMEOUT);
      customElements.whenDefined('gls-dpm-dialog').then(() => {
        clearTimeout(timer);
        resolve(undefined);
      });
    }).catch((error) => {
      glsLoading = null;
      throw error;
    });
  }
  return glsLoading;
}

/** A GLS nagybetűs településneve olvasható formában: „BUDAPEST VII. KER.” → „Budapest VII. ker.” */
function formatCity(contact) {
  return String(contact?.city || '')
    .toLocaleLowerCase('hu')
    .replace(/(^|[\s.-])(\p{L})/gu, (m, sep, ch) => sep + ch.toLocaleUpperCase('hu'))
    .replace(/\bKer\./g, 'ker.')
    // Budapesti kerület római számmal: „Vii. ker.” → „VII. ker.”
    .replace(/\b([IVXLC][ivxlc]*)\.(?=\s*ker\.)/g, (m, roman) => `${roman.toUpperCase()}.`);
}

function formatAddress(contact) {
  if (!contact) return '';
  return [contact.postalCode, formatCity(contact)].filter(Boolean).join(' ') + (contact.address ? `, ${contact.address}` : '');
}

/** @type {HTMLElement & {showModal?: () => void, close?: () => void} | null} */
let glsDialog = null;

async function openGlsMap(root) {
  const mode = POINT_MODES[state.mode];
  if (!mode || mode.carrier !== 'GLS') return;
  const button = root.querySelector('[data-pf-pickup-open]');
  if (button instanceof HTMLButtonElement) button.disabled = true;
  try {
    await loadGls(root.dataset.glsSrc || 'https://map.gls-hungary.com/widget/gls-dpm.js');
  } catch {
    showError(
      'A GLS térkép nem töltődött be. Ha reklám- vagy követésblokkolót használsz, engedélyezd ezen az oldalon, majd próbáld újra – vagy válassz házhozszállítást.',
      root
    );
    if (button instanceof HTMLButtonElement) button.disabled = false;
    return;
  }
  if (button instanceof HTMLButtonElement) button.disabled = false;

  if (!glsDialog) {
    glsDialog = /** @type {any} */ (document.createElement('gls-dpm-dialog'));
    glsDialog.setAttribute('country', 'hu');
    glsDialog.setAttribute('language', 'hu');
    glsDialog.addEventListener('change', (event) => {
      const point = /** @type {any} */ (event).detail;
      if (!point || !point.id) return;
      const current = POINT_MODES[state.mode];
      // A térkép szűrője a módhoz igazodik; ha mégis más típusú pontot adna vissza, a módot igazítjuk hozzá.
      if (current && point.type && point.type !== current.filter) {
        const match = Object.entries(POINT_MODES).find(([, m]) => m.carrier === 'GLS' && m.filter === point.type);
        if (match) state.mode = match[0];
      }
      state.point = {
        id: String(point.id),
        name: String(point.name || ''),
        address: formatAddress(point.contact),
        // a pénztár szállítási címéhez
        zip: String(point.contact?.postalCode || ''),
        city: plainCity(formatCity(point.contact)),
        street: String(point.contact?.address || ''),
      };
      showError('');
      render();
      save();
      glsDialog?.close?.();
    });
    document.body.appendChild(glsDialog);
  }
  glsDialog.setAttribute('filter-type', mode.filter);
  glsDialog.showModal?.();
}

/* ---------- MPL csomagautomata ---------- */

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]);
}

function normalize(value) {
  return String(value).normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

let leafletLoading = null;
function loadLeaflet() {
  if (/** @type {any} */ (window).L?.map) return Promise.resolve();
  if (!leafletLoading) {
    leafletLoading = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET.css;
      document.head.appendChild(link);
      const script = document.createElement('script');
      script.src = LEAFLET.js;
      script.async = true;
      script.onload = () => resolve(undefined);
      script.onerror = () => reject(new Error('Leaflet'));
      document.head.appendChild(script);
    }).catch((error) => {
      leafletLoading = null;
      throw error;
    });
  }
  return leafletLoading;
}

/** @type {Promise<Array<{id: string, name: string, zip: string, city: string, street: string, lat: number, lon: number, hours: string, search: string, place: string}>> | null} */
/** Adatforrásonként egy betöltés (automaták / posták és PostaPontok). @type {Map<string, Promise<any[]>>} */
const mplPointsLoading = new Map();
function loadMplPoints(src) {
  if (!mplPointsLoading.has(src)) {
    const loading = fetch(src)
      .then((response) => {
        if (!response.ok) throw new Error(`MPL lista ${response.status}`);
        return response.json();
      })
      .then((data) =>
        data.pontok.map(([id, name, zip, city, street, lat, lon, hours]) => ({
          id: String(id),
          name,
          zip,
          city,
          street,
          lat,
          lon,
          hours,
          search: normalize(`${zip} ${city} ${street} ${name}`),
          place: normalize(`${zip} ${city}`),
        }))
      )
      .catch((error) => {
        mplPointsLoading.delete(src);
        throw error;
      });
    mplPointsLoading.set(src, loading);
  }
  return /** @type {Promise<any[]>} */ (mplPointsLoading.get(src));
}

const mpl = {
  /** @type {HTMLDialogElement | null} */ dialog: null,
  /** @type {any} */ map: null,
  /** @type {Map<string, any>} */ markers: new Map(),
  /** @type {Array<any>} */ points: [],
  /** @type {any} */ layer: null,
  filter: '',
  countLabel: '',
  activeId: '',
  query: '',
};

function mplAddress(point) {
  return `${[point.zip, point.city].filter(Boolean).join(' ')}, ${point.street}`;
}

function buildMplDialog() {
  const dialog = document.createElement('dialog');
  dialog.className = 'pf-mpl';
  dialog.setAttribute('aria-label', 'MPL csomagautomata kiválasztása');
  dialog.innerHTML = `
    <div class="pf-mpl__inner">
      <div class="pf-mpl__head">
        <p class="pf-mpl__title">MPL csomagautomata kiválasztása</p>
        <button type="button" class="pf-mpl__close" data-pf-mpl-close aria-label="Bezárás">×</button>
      </div>
      <div class="pf-mpl__side">
        <input type="search" class="pf-mpl__search" data-pf-mpl-search placeholder="Település, irányítószám, cím" aria-label="Keresés település, irányítószám vagy cím szerint" autocomplete="off">
        <p class="pf-mpl__count" data-pf-mpl-count>Betöltés…</p>
        <ul class="pf-mpl__list" data-pf-mpl-list></ul>
      </div>
      <div class="pf-mpl__map" data-pf-mpl-map></div>
    </div>`;
  dialog.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target === dialog || target.closest('[data-pf-mpl-close]')) return dialog.close();
    const pick = target.closest('[data-pf-mpl-pick]');
    if (pick instanceof HTMLElement) return pickMplPoint(pick.dataset.pfMplPick || '');
    const item = target.closest('[data-pf-mpl-item]');
    if (item instanceof HTMLElement) focusMplPoint(item.dataset.pfMplItem || '', true);
  });
  dialog.addEventListener('keydown', (event) => {
    const item = event.target instanceof Element ? event.target.closest('[data-pf-mpl-item]') : null;
    if (!(item instanceof HTMLElement) || item !== event.target || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    if (mpl.activeId === item.dataset.pfMplItem) pickMplPoint(mpl.activeId);
    else focusMplPoint(item.dataset.pfMplItem || '', true);
  });
  let timer = 0;
  dialog.querySelector('[data-pf-mpl-search]')?.addEventListener('input', (event) => {
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      mpl.query = /** @type {HTMLInputElement} */ (event.target).value.trim();
      renderMplList(true);
    }, 150);
  });
  document.body.appendChild(dialog);
  return dialog;
}

function distance2(point, lat, lon) {
  const dLat = point.lat - lat;
  const dLon = (point.lon - lon) * Math.cos((lat * Math.PI) / 180);
  return dLat * dLat + dLon * dLon;
}

/** A lista: keresésnél a találatok, üres keresésnél a térkép közepéhez legközelebbi automaták. */
function renderMplList(fromSearch = false) {
  const dialog = mpl.dialog;
  if (!dialog || !mpl.points.length) return;
  const list = dialog.querySelector('[data-pf-mpl-list]');
  const count = dialog.querySelector('[data-pf-mpl-count]');
  if (!list || !count) return;
  const center = mpl.map ? mpl.map.getCenter() : { lat: BUDAPEST[0], lng: BUDAPEST[1] };
  let items = mpl.points;
  const tokens = normalize(mpl.query).split(/\s+/).filter(Boolean);
  if (tokens.length) items = items.filter((p) => tokens.every((t) => (/^\d+$/.test(t) ? p.zip.startsWith(t) || p.search.includes(t) : p.search.includes(t))));
  if (tokens.length && fromSearch && items.length && mpl.map && dialog.open) {
    // A térkép a legjobb (település szerinti) találatokra álljon, ne a csak utcanévben egyezőkre.
    const best = items.filter((p) => tokens.some((t) => p.place.split(/[\s,.]+/).some((word) => word.startsWith(t))));
    const bounds = window.L.latLngBounds((best.length ? best : items).slice(0, 200).map((p) => [p.lat, p.lon]));
    if (bounds.isValid()) mpl.map.fitBounds(bounds, { maxZoom: 14, padding: [24, 24] });
  }
  // Keresésnél előbb a település/irányítószám szerinti egyezés, aztán a névben, végül csak az utcában szereplő; azon belül távolság.
  const rank = (p) => {
    if (!tokens.length) return 0;
    const inPlace = tokens.filter((t) => p.place.split(/[\s,.]+/).some((word) => word.startsWith(t))).length;
    const name = normalize(p.name);
    const inName = tokens.every((t) => name.includes(t)) ? 0 : 1;
    // Teljes szóra egyező névrész előrébb: „Szeged 1 posta” ≠ „Szeged 15 …”
    const nameWords = name.split(/[\s,.\-–]+/);
    const exactWords = tokens.filter((t) => nameWords.includes(t)).length;
    return (tokens.length - inPlace) * 100 + (tokens.length - exactWords) * 10 + inName;
  };
  const sorted = [...items]
    .sort((a, b) => rank(a) - rank(b) || distance2(a, center.lat, center.lng) - distance2(b, center.lat, center.lng))
    .slice(0, 60);
  count.textContent = tokens.length ? `${items.length} találat` : `${mpl.points.length} ${mpl.countLabel} · a térkép közepéhez legközelebbiek`;
  list.innerHTML = sorted.length
    ? sorted
        .map((p) => {
          const active = p.id === mpl.activeId;
          return `<li><div class="pf-mpl__item${active ? ' is-active' : ''}" data-pf-mpl-item="${escapeHtml(p.id)}" role="button" tabindex="0">
            <strong>${escapeHtml(p.name)}</strong>
            <span>${escapeHtml(mplAddress(p))}</span>
            ${p.hours ? `<small>Nyitva: ${escapeHtml(p.hours)}</small>` : ''}
            ${active ? `<button type="button" class="pf-mpl__pick" data-pf-mpl-pick="${escapeHtml(p.id)}">Ezt választom</button>` : ''}
          </div></li>`;
        })
        .join('')
    : '<li class="pf-mpl__count">Nincs találat. Próbáld irányítószámmal vagy település nevével.</li>';
}

function focusMplPoint(id, fromList = false) {
  const point = mpl.points.find((p) => p.id === id);
  if (!point) return;
  mpl.activeId = id;
  if (mpl.map) {
    mpl.map.setView([point.lat, point.lon], Math.max(mpl.map.getZoom(), 15));
    mpl.markers.get(id)?.openPopup();
  }
  renderMplList();
  if (fromList) mpl.dialog?.querySelector('.pf-mpl__item.is-active')?.scrollIntoView({ block: 'nearest' });
}

function pickMplPoint(id) {
  const point = mpl.points.find((p) => p.id === id);
  if (!point) return;
  if (POINT_MODES[state.mode]?.filter !== mpl.filter) {
    const match = Object.entries(POINT_MODES).find(([, m]) => m.filter === mpl.filter);
    if (match) state.mode = match[0];
  }
  state.point = {
    id: point.id,
    name: point.name,
    address: mplAddress(point),
    // a pénztár szállítási címéhez (Budapesten a hivatalos irányítószám-jegyzékből; ahol az nem egyértelmű, üres, és a vásárló adja meg)
    zip: point.zip,
    city: plainCity(point.city),
    street: point.street,
  };
  showError('');
  render();
  save();
  mpl.dialog?.close();
}

async function openMplMap(root) {
  if (!mpl.dialog) mpl.dialog = buildMplDialog();
  const dialog = mpl.dialog;
  const mode = POINT_MODES[state.mode];
  if (!mode || mode.carrier !== 'MPL') return;
  const title = dialog.querySelector('.pf-mpl__title');
  if (title) title.textContent = mode.title;
  dialog.setAttribute('aria-label', mode.title);
  if (!dialog.open) dialog.showModal();
  let points;
  try {
    [points] = await Promise.all([loadMplPoints(root.dataset[mode.src] || ''), loadLeaflet()]);
  } catch {
    const list = dialog.querySelector('[data-pf-mpl-list]');
    const count = dialog.querySelector('[data-pf-mpl-count]');
    if (count) count.textContent = '';
    if (list)
      list.innerHTML =
        '<li class="pf-mpl__error">Az MPL térkép nem töltődött be. Ellenőrizd az internetkapcsolatot (vagy a reklámblokkolót), majd próbáld újra – vagy válassz más szállítási módot.</li>';
    return;
  }
  const L = /** @type {any} */ (window).L;
  if (!mpl.map) {
    // Canvas-rajzolás: több ezer pont is gyorsan megjelenik
    mpl.map = L.map(dialog.querySelector('[data-pf-mpl-map]'), { zoomControl: true, preferCanvas: true }).setView(BUDAPEST, 12);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>',
    }).addTo(mpl.map);
    mpl.map.on('moveend', () => {
      if (!mpl.query) renderMplList();
    });
  }
  // Másik ponttípusra váltáskor (automata ↔ posta/PostaPont) a jelölőket és a keresést újraépítjük.
  if (mpl.filter !== mode.filter) {
    mpl.filter = mode.filter;
    mpl.countLabel = mode.countLabel;
    mpl.points = points;
    mpl.activeId = '';
    mpl.query = '';
    const search = /** @type {HTMLInputElement | null} */ (dialog.querySelector('[data-pf-mpl-search]'));
    if (search) search.value = '';
    mpl.layer?.remove();
    mpl.layer = L.layerGroup().addTo(mpl.map);
    mpl.markers.clear();
    for (const p of mpl.points) {
      const marker = L.circleMarker([p.lat, p.lon], { radius: 8, color: '#fff', weight: 2, fillColor: MPL_COLOR, fillOpacity: 1 })
        .bindPopup(
          `<strong>${escapeHtml(p.name)}</strong><br>${escapeHtml(mplAddress(p))}${p.hours ? `<br><small>Nyitva: ${escapeHtml(p.hours)}</small>` : ''}<br><button type="button" class="pf-mpl__pick" data-pf-mpl-pick="${escapeHtml(p.id)}">Ezt választom</button>`
        )
        .on('click', () => {
          mpl.activeId = p.id;
          renderMplList();
        })
        .addTo(mpl.layer);
      mpl.markers.set(p.id, marker);
    }
  }
  // A modális ablak megnyitása után a térkép méretét újra kell számolni.
  requestAnimationFrame(() => {
    mpl.map.invalidateSize();
    const current = state.point && POINT_MODES[state.mode]?.carrier === 'MPL' ? mpl.points.find((p) => p.id === state.point?.id) : null;
    if (current) focusMplPoint(current.id);
    else renderMplList();
  });
  /** @type {HTMLInputElement | null} */ (dialog.querySelector('[data-pf-mpl-search]'))?.focus({ preventScroll: true });
}

function openMap(root) {
  const mode = POINT_MODES[state.mode];
  if (!mode) return;
  if (mode.carrier === 'MPL') openMplMap(root);
  else openGlsMap(root);
}

function onModeChange(input) {
  const previous = POINT_MODES[state.mode];
  const next = POINT_MODES[input.value];
  state.mode = input.value;
  // Más szolgáltatóra/típusra váltáskor a korábbi pont már nem érvényes.
  if (!next || !previous || previous.carrier !== next.carrier || previous.filter !== next.filter) state.point = null;
  showError('');
  render();
  save();
}

/** Továbblépés ellenőrzése: mód kell, csomagpontos módnál pont is. */
function validate() {
  if (!state.mode) return 'Válaszd ki, hogyan kéred a csomagot.';
  if (POINT_MODES[state.mode] && !state.point) return 'Válaszd ki az átvételi pontot a térképen.';
  return '';
}

function guard(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target || !roots().length) return;
  const button =
    event.type === 'submit'
      ? /** @type {SubmitEvent} */ (event).submitter
      : target.closest('button[name="checkout"], input[name="checkout"]');
  if (!(button instanceof HTMLElement) || button.getAttribute('name') !== 'checkout') return;

  const scope = button.closest('cart-drawer-component, dialog, .cart-drawer, main, body') || document.body;
  const root = scope.querySelector('[data-pf-pickup]') || roots()[0];
  const message = validate();
  if (message) {
    event.preventDefault();
    event.stopImmediatePropagation();
    showError(message, root);
    root?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    return;
  }
  if (pending) {
    // Még fut a mentés: megvárjuk, majd újra elindítjuk a fizetést.
    event.preventDefault();
    event.stopImmediatePropagation();
    pending.then(() => button.click()).catch(() => {});
  }
}

function init() {
  readInitialState();
  render();
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.matches('[data-pf-pickup-mode]')) onModeChange(input);
  });
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-pf-pickup-open]') : null;
    const root = button?.closest('[data-pf-pickup]');
    if (root instanceof HTMLElement) openMap(root);
  });
  window.addEventListener('click', guard, true);
  window.addEventListener('submit', guard, true);
  // Kosárváltozáskor (mennyiség, törlés, új tétel) a Shopify újraszámolja a díjakat.
  document.addEventListener('cart:update', () => {
    scheduleRates();
    // Kosárváltozáskor a jelölőt és a bejelölt módot igazítjuk (pl. üres kosárból a jelölő kikerül).
    const sync = syncCheckout().catch(() => {});
    pending = sync;
    sync.finally(() => {
      if (pending === sync) pending = null;
    });
  });
  scheduleRates();
  // A mentett választáshoz igazítjuk a kosár jelölőjét (pl. korábbi látogatásból maradt választás).
  if (roots().length) {
    const sync = syncCheckout().catch(() => {});
    pending = sync;
    sync.finally(() => {
      if (pending === sync) pending = null;
    });
  }
  watchChatOverlap();
  // A kosárfiók „Tovább a fizetéshez” gombja ide visz (/cart#szallitas): a választót középre görgetjük.
  if (location.hash === '#szallitas') {
    window.setTimeout(() => roots()[0]?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 400);
  }
  // A Horizon újrarendereli a kosarat (mennyiség, törlés): az új példányokat az állapothoz igazítjuk.
  new MutationObserver(() => {
    const stale = roots().some(
      (root, index) =>
        root.dataset.mode !== state.mode ||
        root.dataset.pointId !== (state.point?.id || '') ||
        root.querySelector(`[data-pf-pickup-mode]:not([name="pf-pickup-mode-${index}"])`)
    );
    if (stale) render();
  }).observe(document.body, { childList: true, subtree: true });
}

/** Az Inbox gombja egymásba ágyazott shadow root-okban van (shopify-chat → shopify-agent-runtime → .activator). */
function findActivator(host) {
  const queue = [host.shadowRoot];
  while (queue.length) {
    const root = queue.shift();
    if (!root) continue;
    const found = root.querySelector('.activator, [part~="activator"]');
    if (found) return found;
    root.querySelectorAll('*').forEach((el) => el.shadowRoot && queue.push(el.shadowRoot));
  }
  return null;
}

/** A csevegésgomb által nem takarható elemek: a kosároldal szállításválasztója és a főoldali figurakártyák „+” gombjai. */
const CHAT_AVOID = [
  '.pf-pickup__mode',
  '[data-pf-fee]',
  '[data-pf-pickup-chosen]',
  '[data-pf-pickup-open]',
  '[data-pf-summary] dd',
  '.pf-pickup__error',
  '.cart__checkout-button',
  '.pfx-friends .btn',
].join(', ');

/**
 * A Shopify Inbox lebegő „Csevegés” gombja (jobb alsó sarok) a szállításválasztó elemei vagy a főoldali
 * figurakártyák gombjai fölé kerülhet.
 * Csak addig rejtjük el, amíg ténylegesen takarná valamelyiket (mód, díj, választott pont, gomb, összesítő,
 * fizetés gomb); görgetés után visszajön. Mobilon és asztali nézetben is.
 */
function watchChatOverlap() {
  let queued = false;
  const check = () => {
    queued = false;
    const host = document.querySelector('shopify-chat');
    if (!host) return;
    let covers = false;
    const button = findActivator(host);
    if (button) {
      const b = button.getBoundingClientRect();
      // A gomb elrejtése után is a helyét mérjük (a mérete megmarad), 8 px ráhagyással.
      covers = [...document.querySelectorAll(CHAT_AVOID)].some((el) => {
        const r = el.getBoundingClientRect();
        return r.width > 0 && r.right > b.left - 8 && r.left < b.right + 8 && r.bottom > b.top - 8 && r.top < b.bottom + 8;
      });
    }
    host.classList.toggle('pf-chat-hidden', covers);
  };
  const queue = () => {
    if (queued) return;
    queued = true;
    window.setTimeout(check, 50); // időzítő (nem requestAnimationFrame): háttérben lévő lapon is lefut
  };
  window.addEventListener('scroll', queue, { passive: true });
  window.addEventListener('resize', queue);
  window.setInterval(queue, 1000); // a gomb később töltődik be, és a kosár is újrarenderelődhet
  queue();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
